import React, { useState, useEffect } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { connect } from 'react-redux'
import { PLANS, PLANS_ID, getMergedPlan } from '../../constants/plans'
import { switchPlan } from '../../services/manage'
import { downgradeSubscription } from '../../services/email-list'
import { ErrorBox } from 'components/ErrorBox/ErrorBox'
import PlanView from './components/PlanView'
import SuccessView from './components/SuccessView'
import UpgradeFailedView from './components/UpgradeFailedView'
import { getAreaCurrency, TIMEZONE } from 'constants/currency'
import { getErrorMessage, getUpgradeReason, isOnFreeTrial } from 'utils/general'
import * as Sentry from '@sentry/react'
import { ERROR_CODE_MAP } from '../../stripe.constants'
import { getPayProCheckoutUrl } from 'services/manage'
import CustomModal from 'components/CustomModal/CustomModal'
import { fetchUserData } from 'Manage/actions'
import { useStripe } from '@stripe/react-stripe-js'
import { handle3DSecure } from 'utils/payment'
import styles from './UpgradePayModal.module.css'
import { getPaymentProvider } from 'utils/paymentProvider'

export const CURRENT_PLAN = 'current_plan'
export const UPGRADE = 'upgrade_plan'
export const DOWNGRADE = 'downgrade_plan'

export const EmailSent = ({ history, onHide, userProfile }) => {
  const isEligibleForAutomatedDowngrade =
    `${userProfile?.customerId}`?.startsWith('cus_') || `${userProfile?.customerId}`?.startsWith('ctm_')
  return (
    <div className="p-4">
      <Modal.Header className={styles.header}>
        <Modal.Title className={styles.title}>
          {isEligibleForAutomatedDowngrade ? 'Your downgrade is scheduled for your next billing cycle' : 'Success'}
        </Modal.Title>
      </Modal.Header>
      {isEligibleForAutomatedDowngrade ? (
        <div>
          <div className="flex flex-column align-items-center mb-4">
            <img className="mb-3" height="40px" width="40px" alt="check" src="/check.png" />
            <div className="text-center">
              <span>Subscription downgrades happen in your billing month</span>
              <br />
              <span>We will notify you on the day of your downgrade</span>
            </div>
          </div>
          <Button
            className="w-100"
            onClick={() => {
              history.push('/'), onHide()
            }}
          >
            Done
          </Button>
        </div>
      ) : (
        <div>
          <div className="flex flex-column align-items-center mb-4">
            <img className="mb-3" height="40px" width="40px" alt="check" src="/check.png" />
            <span className="text-center">We're processing your request and will email you when it's complete.</span>
          </div>
          <Button
            className="w-100"
            onClick={() => {
              history.push('/'), onHide()
            }}
          >
            Done
          </Button>
        </div>
      )}
    </div>
  )
}

const REQUEST = 'REQUEST'
const SUCCESS = 'SUCCESS'
const FAILED = 'FAILED'
const EMAIL_SENT = 'EMAIL_SENT'
const RETRY_PAYMENT = 'RETRY_PAYMENT'

const UpgradePayModal = ({
  history,
  email,
  show,
  onHide,
  currentPlanId = PLANS.PRO.id,
  periodProp = 'monthly',
  upgradePlanId,
  interval,
  currency,
  userProfile,
  dispatch
}) => {
  const [stage, setStage] = useState(REQUEST)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState()
  const [selectedPeriod, setSelectedPeriod] = useState(periodProp)

  const getNextHigherPlan = (currentPlanId) => {
    const planOrder = {
      [PLANS.TINY.id]: PLANS.PRO.id,
      [PLANS.PRO.id]: PLANS.PRO_U.id,
      [PLANS.PRO_U.id]: PLANS.PRO_MAX.id,
      [PLANS.PRO_MAX.id]: PLANS.PRO_MAX.id
    }
    return planOrder[currentPlanId]
  }

  const getDefaultPlan = () => {
    return upgradePlanId || getNextHigherPlan(currentPlanId)
  }

  const defaultPlanId = getDefaultPlan()
  const initialPlan = PLANS_ID[defaultPlanId] || (currentPlanId === PLANS.TINY.id ? PLANS.PRO : PLANS.PRO_U)
  const stripe = useStripe()

  const isStripeCustomer = `${userProfile?.customerId}`?.startsWith('cus_')

  const userCurrency = userProfile?.currency || getAreaCurrency(TIMEZONE)
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [currentCurrency, setCurrentCurrency] = useState(userCurrency)

  const onPlanChange = (id) => {
    const planWithCurrencyPricing = getMergedPlan(id, currentCurrency)
    setSelectedPlan(planWithCurrencyPricing)
  }

  const handlePayProUpgradeCheckout = async (period, amount) => {
    const {
      data: { url }
    } = await getPayProCheckoutUrl({
      payProId: selectedPlan?.prices?.[period]?.payPro?.priceId,
      email,
      upgradeAmount: amount
    })
    window.open(url, '_blank')
  }

  const handle3DSecureLocal = async (paymentData, onSuccessCallback) => {
    await handle3DSecure(
      stripe,
      paymentData,
      email,
      () => {
        setIsLoading(false)
        if (onSuccessCallback) {
          onSuccessCallback()
        } else {
          setStage(SUCCESS)
        }
      },
      (errorMessage) => {
        setErrorMsg(errorMessage)
        setIsLoading(false)
        setStage(FAILED)
      }
    )
  }

  const urlParams = new URLSearchParams(window.location.search)
  const status = urlParams.get('status')

  useEffect(() => {
    if (show) {
      const { pathname } = window.location
      if (pathname === '/manage/subscription/upgrade') {
        history.replace({ pathname: '/manage/subscription/upgrade' })
        return
      }

      if (pathname === '/manage/subscription/change') {
        history.replace({ pathname: '/manage/subscription/change' })
        return
      }

      history.replace({ pathname: '/manage/subscription/change' })
    }
  }, [show, history])

  const onConfirm = ({ plan, period }) => {
    setIsLoading(true)
    setSelectedPeriod(period)

    const upgradeReason = getUpgradeReason()
    const isFreeTrial = isOnFreeTrial(userProfile)

    const formData = {
      email,
      plan,
      priceId: selectedPlan?.prices?.[period]?.stripe?.priceId,
      paddleBillingId: selectedPlan?.prices?.[period]?.paddle?.priceId,
      payProId: selectedPlan?.prices?.[period]?.payPro?.priceId,
      productId: selectedPlan?.id,
      upgradeReason,
      ...(isFreeTrial && { endTrial: true })
    }

    switchPlan(formData)
      .then(({ data }) => {
        if (data?.amount) {
          handlePayProUpgradeCheckout(period, data.amount)
          setIsLoading(false)
        } else if (data?.requires_action) {
          // Use the reusable 3D Secure handler
          handle3DSecureLocal(data)
        } else {
          setIsLoading(false)
          setStage(SUCCESS)
        }
      })
      .catch((error) => {
        const declineCode = ERROR_CODE_MAP[error?.declineCode]
        const errorMessage =
          declineCode ||
          'Sorry, unable to change your plan. This is likely an issue with your card. Please contact your bank or our support.'
        setErrorMsg(errorMessage)

        if (!declineCode) {
          Sentry.captureException(error, {
            tags: {
              section: 'upgrade-account-error'
            }
          })
        }

        setIsLoading(false)
        if (isStripeCustomer) {
          const retryPlan = getMergedPlan(selectedPlan.id, currentCurrency)
          setSelectedPlan(retryPlan)
          setStage(RETRY_PAYMENT)
        } else {
          setStage(FAILED)
        }
      })
  }

  const onDowngradeSubscription = async (selectedPlan, planPrices = {}, period) => {
    const message = `${selectedPlan?.label} ${period}`
    const priceId = planPrices?.priceId
    const paddleBillingId = planPrices?.paddleBillingId
    setIsLoading(true)
    try {
      await downgradeSubscription({ email, priceId, paddleBillingId, message })
      setStage(EMAIL_SENT)
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      setStage(FAILED)
      setErrorMsg(getErrorMessage(error))
    }
  }

  const handleRetryWithPreviousMethod = () => {
    setStage(REQUEST)
    setErrorMsg(undefined)
  }

  const handleUpgradeSuccess = () => {
    setStage(SUCCESS)
  }

  useEffect(() => {
    if (defaultPlanId) {
      const planWithCurrencyPricing = getMergedPlan(initialPlan.id, currentCurrency)
      setSelectedPlan(planWithCurrencyPricing)
    }
  }, [defaultPlanId, currentCurrency])

  useEffect(() => {
    if (status === 'success') {
      setStage(SUCCESS)
    }
  }, [status])

  const handleClose = () => {
    onHide()
    dispatch(fetchUserData())
    history.replace({ pathname: '/manage' })
    setSelectedPlan(null)
    setStage(REQUEST)
    setErrorMsg(undefined)
  }

  return (
    <CustomModal
      show={show}
      onHide={handleClose}
      aria-labelledby="contained-modal-title-vcenter"
      size={stage === RETRY_PAYMENT || stage === EMAIL_SENT ? 'md' : 'lg'}
      dialogClassName={styles.dialog}
      centered
      enforceFocus={false}
    >
      {stage === REQUEST && selectedPlan && (
        <PlanView
          isLoading={isLoading}
          currentPlanId={currentPlanId}
          defaultPlan={PLANS_ID[defaultPlanId]}
          periodProp={periodProp}
          onConfirm={onConfirm}
          onDowngradeSubscription={onDowngradeSubscription}
          interval={interval}
          onPlanChange={onPlanChange}
          selectedPlan={selectedPlan}
          handleClose={handleClose}
        />
      )}
      {stage === SUCCESS && <SuccessView history={history} onHide={handleClose} />}
      {stage === EMAIL_SENT && <EmailSent history={history} onHide={handleClose} userProfile={userProfile} />}
      {stage === RETRY_PAYMENT && (
        <UpgradeFailedView
          dispatch={dispatch}
          email={email}
          selectedPlan={selectedPlan}
          period={selectedPeriod}
          errorMsg={errorMsg}
          onRetryWithPreviousMethod={handleRetryWithPreviousMethod}
          onUpgradeSuccess={handleUpgradeSuccess}
          userProfile={userProfile}
          handle3DSecure={handle3DSecureLocal}
        />
      )}
      {stage === FAILED && (
        <ErrorBox
          message={errorMsg || 'Sorry, unable to change your plan'}
          onClick={() => {
            setStage(REQUEST)
            setErrorMsg(undefined)
          }}
        />
      )}
    </CustomModal>
  )
}

const mapDispatchToProps = (dispatch) => ({ dispatch })

export default connect(null, mapDispatchToProps)(UpgradePayModal)
